import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Heading } from '@library';
import type { ProductItemOrVariant, ProductVariant } from '../../types';
import ProductStockIndicator from '../../../availability/components/ProductStockIndicator';
import useLoginState from '../../../core/hooks/useLoginState';
import Divider from '../../../core/components/Divider';
import ProductAttributeTitle from './ProductAttributeTitle';
import styles from './ProductAvailability.module.css';
import ReplacementProducts from './ReplacementProducts/ReplacementProducts';

interface ProductAvailabilityContentProps {
    variant?: ProductVariant;
    hasVariants: boolean;
    desiredQuantity?: number;
    sku?: string;
}

const ProductAvailabilityContent = memo(
    ({
        variant,
        hasVariants,
        desiredQuantity,
        sku,
    }: ProductAvailabilityContentProps) => {
        const isLoggedIn = useLoginState();

        if (!isLoggedIn) {
            return (
                <Heading h6>
                    <FormattedMessage id="please login to check availability" />
                </Heading>
            );
        }

        if (!variant && hasVariants) {
            return (
                <div className={styles.availabilitySelectSizeMessage}>
                    <Heading h6>
                        <FormattedMessage id="please choose a size" />
                    </Heading>
                </div>
            );
        }

        return (
            <ProductStockIndicator
                sku={sku}
                desiredQuantity={desiredQuantity}
                messageStyle="right"
                vertical
            />
        );
    }
);

const ProductAvailability = ({
    replacementProducts = [],
    ...props
}: ProductAvailabilityContentProps & {
    replacementProducts?: ProductItemOrVariant[];
}) => {
    return (
        <div>
            <div className={styles.productAvailabilityPaddedContent}>
                <ProductAttributeTitle>
                    <FormattedMessage id="availability" />
                </ProductAttributeTitle>
            </div>
            <Divider lengthPercentage={100} />
            <div className={styles.productAvailabilityPaddedContent}>
                <ProductAvailabilityContent {...props} />
                {replacementProducts.length > 0 && (
                    <div className={styles.productReplacementDetailsWrapper}>
                        <ReplacementProducts products={replacementProducts} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(ProductAvailability);
